import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Languages, Globe, Check } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * LanguagePickerModal — 切換 avatar 語系
 *
 * 流程：
 *   1. 選主要語系（dropdown，31 個 Gemini Live 支援語系，分組）
 *   2. 勾「多國語系」→ AI 依用戶語音 mirror 回應
 *   3. 套用 → 觸發 set-ivr-language skill（翻譯所有 prompt + 重生 MP3）
 */

const LANGUAGE_OPTIONS = [
    { value: 'zh-TW', label: '🇹🇼 繁體中文（預設）', group: 'common' },
    { value: 'en',    label: '🇺🇸 English',          group: 'common' },
    { value: 'ja',    label: '🇯🇵 日本語',            group: 'common' },
    { value: 'ko',    label: '🇰🇷 한국어',            group: 'common' },
    { value: 'th',    label: '🇹🇭 ไทย',              group: 'asia' },
    { value: 'vi',    label: '🇻🇳 Tiếng Việt',       group: 'asia' },
    { value: 'id',    label: '🇮🇩 Bahasa Indonesia', group: 'asia' },
    { value: 'ms',    label: '🇲🇾 Bahasa Melayu',    group: 'asia' },
    { value: 'fil',   label: '🇵🇭 Filipino',         group: 'asia' },
    { value: 'hi',    label: '🇮🇳 हिन्दी',             group: 'asia' },
    { value: 'ta',    label: '🇮🇳 தமிழ்',             group: 'asia' },
    { value: 'es',    label: '🇪🇸 Español',          group: 'europe' },
    { value: 'pt',    label: '🇵🇹 Português',        group: 'europe' },
    { value: 'fr',    label: '🇫🇷 Français',         group: 'europe' },
    { value: 'de',    label: '🇩🇪 Deutsch',          group: 'europe' },
    { value: 'it',    label: '🇮🇹 Italiano',         group: 'europe' },
    { value: 'nl',    label: '🇳🇱 Nederlands',       group: 'europe' },
    { value: 'pl',    label: '🇵🇱 Polski',           group: 'europe' },
    { value: 'sv',    label: '🇸🇪 Svenska',          group: 'europe' },
    { value: 'ro',    label: '🇷🇴 Română',           group: 'europe' },
    { value: 'el',    label: '🇬🇷 Ελληνικά',         group: 'europe' },
    { value: 'cs',    label: '🇨🇿 Čeština',          group: 'europe' },
    { value: 'da',    label: '🇩🇰 Dansk',            group: 'europe' },
    { value: 'fi',    label: '🇫🇮 Suomi',            group: 'europe' },
    { value: 'bg',    label: '🇧🇬 Български',        group: 'europe' },
    { value: 'hr',    label: '🇭🇷 Hrvatski',         group: 'europe' },
    { value: 'sk',    label: '🇸🇰 Slovenčina',       group: 'europe' },
    { value: 'ar',    label: '🇸🇦 العربية',           group: 'other' },
    { value: 'ru',    label: '🇷🇺 Русский',          group: 'other' },
    { value: 'tr',    label: '🇹🇷 Türkçe',           group: 'other' },
    { value: 'uk',    label: '🇺🇦 Українська',       group: 'other' },
];

export function LanguagePickerModal({ open, onClose, onApply, isStreaming, avatarName, currentLanguage, currentMultiLingual, newMode = null }) {
    const { t } = useTranslation('wsModals');
    const [language, setLanguage] = useState(currentLanguage || 'zh-TW');
    const [multiLingual, setMultiLingual] = useState(!!currentMultiLingual);
    const isSurvey = newMode === 'survey';

    useEffect(() => {
        if (open) {
            setLanguage(currentLanguage || 'zh-TW');
            setMultiLingual(!!currentMultiLingual);
        }
    }, [open, currentLanguage, currentMultiLingual]);

    if (!open) return null;

    const handleApply = () => {
        if (isStreaming) return;
        const langChanged = language !== (currentLanguage || 'zh-TW');
        const multiChanged = multiLingual !== !!currentMultiLingual;
        if (!langChanged && !multiChanged) {
            onClose();
            return;
        }

        const langLabel = LANGUAGE_OPTIONS.find(l => l.value === language)?.label || language;
        const target = multiLingual ? 'multi' : language;

        const roleLabel = isSurvey ? '問卷 AI 訪員' : 'AI 角色';
        const lines = [`請更新這個${roleLabel} (${avatarName || 'avatar'}) 的語系設定:`, ''];
        if (langChanged) {
            lines.push(`- 主要語系：改成「${langLabel}」（code=${language}）`);
        }
        if (multiChanged) {
            lines.push(`- 多國語系：${multiLingual ? '✅ 開啟（AI 依用戶語音 mirror 回應）' : '⚪ 關閉（強制單一語系）'}`);
        }
        lines.push('');

        if (isSurvey) {
            // Survey source-aware i18n:原語系永遠不動,翻譯時從 source 翻 → cache 進 translations[lang] → 設 active
            lines.push('請用 set-ivr-language skill(survey 分支)切語系,**source-aware 流程**:');
            lines.push('');
            lines.push(`✅ **問卷語系切換** target=${language}`);
            lines.push('');
            lines.push('### 第 0 步:Migration(idempotent — 第一次跑會 backup,後續跳過)');
            lines.push('```');
            lines.push(`AVATAR_ID=<avatarId> node scripts/survey-i18n-migrate.js`);
            lines.push('```');
            lines.push('這會把現有 question / customOptions / firstMessage / scenario / surveyEndingScript copy 到 .source 欄位(永不變)。已 migrate 過會直接跳過。');
            lines.push('');
            lines.push('### 第 1 步:用 module/Survey/i18nFields.js helper 判斷該怎麼切');
            lines.push('```js');
            lines.push('const { resolveAvatarForLang, resolveQuestionForLang, applyAvatarTranslation, applyQuestionTranslation, restoreToSource } = require(\'./module/Survey/i18nFields\');');
            lines.push('```');
            lines.push('');
            lines.push(`分流邏輯:`);
            lines.push(`A) **target (${language}) === sourceLanguage** → 呼叫 \`restoreToSource(avatarRef, questionsSnap)\`,從 .source 還原 active,**完全不調 LLM**`);
            lines.push(`B) **target ∈ avatar.i18nTranslations** && question.translations 都有 → 從 cache 還原 active(寫 avatar.{firstMessage, scenario, surveyEndingScript} + question.{question, customOptions} 從 cache),**不調 LLM**`);
            lines.push(`C) **以上都不滿足** → 從 .source 翻譯到 ${language}:`);
            lines.push(`   - avatar 層:翻 source.firstMessage / source.scenario / source.surveyEndingScript`);
            lines.push(`   - 每題:翻 source.question + source.customOptions[].label`);
            lines.push(`   - 寫回:applyAvatarTranslation / applyQuestionTranslation(會同時寫 active + cache 進 translations[${language}])`);
            lines.push('');
            lines.push('### 嚴格規則');
            lines.push(`  ⛔ **絕不從 question.question(已翻譯版)翻成新語系** — 一律從 question.source.question 翻`);
            lines.push(`  ⛔ **絕不動 source.* 欄位**(原文 immutable)`);
            lines.push(`  ⛔ **絕不翻** customOptions[].value(option-0 等內部 code,後端 routing 用)`);
            lines.push(`  ⛔ **絕不動** ivrFlowConfig(survey 沒這個 config)`);
            lines.push('');
            lines.push('### 第 2 步:設 avatar.languageCode');
            lines.push(`  applyAvatarTranslation 已自動寫 languageCode=${language}。restoreToSource 也會寫 languageCode = sourceLanguage。`);
            lines.push('');
            lines.push('### 第 3 步:重生開場白 MP3');
            lines.push(`  跑 generate-ivr-greeting skill,**帶 \`SURVEY_MODE=force\`** → 從新 active firstMessage / canvas Q1 重生 MP3 + 上 S3`);
            lines.push('');
            lines.push('完成後簡單回報「語系切換到 ' + language + ' 完成(N 題 + scenario + firstMessage + MP3,cache_hit=A/B/C)」,不用列細節。');
        } else {
            lines.push('請用 set-ivr-language skill 處理，遵守以下嚴格規則：');
            lines.push('');
            if (multiLingual) {
                lines.push(`✅ **這是 multi 模式**（multiLingual=true，primaryLanguage=${language}）`);
                lines.push('  動作：');
                lines.push('  1. 設 ivrFlowConfig.multiLingual=true');
                lines.push(`  2. 設 ivrFlowConfig.primaryLanguage="${language}"`);
                lines.push(`  3. 設 defaultLanguage="${language}"`);
                lines.push(`  4. 翻譯 greeting 文字 + 重生 MP3 用「${language}」(讓接通時用 primary lang 開場)`);
                lines.push(`  5. **開場白動態設計三段也要翻**：ivrFlowConfig.greetingNight.text / greetingSchedule[].dayText / greetingSchedule[].nightText`);
                lines.push('     翻完寫回後跑 set-ivr-language 的 Step 6b（scheduleGreetingSynth）重生夜間/節慶 MP3，不要自己拿原文重生');
                lines.push('  ⛔ **嚴禁翻譯**：各 node 的 agentInstruction.role/goal/instructions/constraints、scenarios.name/description');
                lines.push('  原因：multi 模式靠 framework template 處理語言切換，per-node prompts 翻譯反而會 anchor model 講某種語言、無法 mirror 用戶');
                lines.push('  ⛔ **嚴禁翻譯**：knowledgeConfig.notFoundMessage 等其他 user-visible 文字也都保留原狀');
                lines.push('  只動：firstMessage + greeting/greetingTemplate/greetingTemplates（開場白）+ greetingMp3 + flag');
            } else {
                lines.push(`✅ **這是單一語系模式**（multiLingual=false，target=${language}）`);
                lines.push('  動作：');
                lines.push(`  1. 翻譯所有 node agentInstruction (role/goal/instructions/constraints) → "${language}"`);
                lines.push(`  2. 翻譯所有 scenarios.name/description / greeting / formCollection labels → "${language}"`);
                lines.push('  3. 設 ivrFlowConfig.multiLingual=false');
                lines.push(`  4. 設 ivrFlowConfig.primaryLanguage="${language}"`);
                lines.push(`  5. 設 defaultLanguage="${language}"`);
                lines.push(`  6. 重生 greeting MP3 用「${language}」`);
                lines.push(`  7. **開場白動態設計三段也要翻**：ivrFlowConfig.greetingNight.text / greetingSchedule[].dayText / greetingSchedule[].nightText`);
                lines.push('     翻完寫回後跑 set-ivr-language 的 Step 6b（scheduleGreetingSynth）重生夜間/節慶 MP3，不要自己拿原文重生');
                lines.push('  ⛔ **不要翻**：tool 名（snake_case）/ KB ID / fields[].name / fields[].options / placeholder {{var}}');
            }
        }
        lines.push('');
        lines.push('完成後簡單回報「語系切換完成」即可，不用列細節。');

        const dParts = [];
        if (langChanged) dParts.push(t('lang.display.primary', { label: langLabel }));
        if (multiChanged) dParts.push(t('lang.display.multi', { state: multiLingual ? t('lang.on') : t('lang.off') }));
        const displayText = `${t('lang.display.title')}\n${dParts.join('\n')}`;

        onApply(lines.join('\n'), { displayText });
        onClose();
    };

    const currentLangLabel = LANGUAGE_OPTIONS.find(l => l.value === language)?.label || language;

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-3">
                        <div className="size-9 rounded-md bg-accent-50 flex items-center justify-center">
                            <Languages size={18} className="text-accent-700" strokeWidth={1.7} />
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">language</div>
                            <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('lang.title')}</h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5">
                    {/* 主要語系 dropdown */}
                    <div>
                        <label className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-ink-800">{t('lang.primaryLabel')}</span>
                            <span className="text-[10px] text-ink-400">{t('lang.primaryHint')}</span>
                        </label>
                        <select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                        >
                            <optgroup label={t('lang.group.common')}>
                                {LANGUAGE_OPTIONS.filter(l => l.group === 'common').map(l => (
                                    <option key={l.value} value={l.value}>{l.label}</option>
                                ))}
                            </optgroup>
                            <optgroup label={t('lang.group.asia')}>
                                {LANGUAGE_OPTIONS.filter(l => l.group === 'asia').map(l => (
                                    <option key={l.value} value={l.value}>{l.label}</option>
                                ))}
                            </optgroup>
                            <optgroup label={t('lang.group.europe')}>
                                {LANGUAGE_OPTIONS.filter(l => l.group === 'europe').map(l => (
                                    <option key={l.value} value={l.value}>{l.label}</option>
                                ))}
                            </optgroup>
                            <optgroup label={t('lang.group.other')}>
                                {LANGUAGE_OPTIONS.filter(l => l.group === 'other').map(l => (
                                    <option key={l.value} value={l.value}>{l.label}</option>
                                ))}
                            </optgroup>
                        </select>
                        <p className="text-[11px] text-ink-500 mt-1.5">
                            {t('lang.current')}<span className="font-medium">{LANGUAGE_OPTIONS.find(l => l.value === (currentLanguage || 'zh-TW'))?.label || currentLanguage}</span>
                        </p>
                    </div>

                    {/* 多國語系 checkbox(survey 隱藏,沒有對應 runtime 邏輯) */}
                    {!isSurvey && (
                    <div className="border-t border-cream-200 pt-4">
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={multiLingual}
                                onChange={(e) => setMultiLingual(e.target.checked)}
                                className="mt-0.5 size-4 rounded border-cream-300 text-accent-600 focus:ring-accent-500"
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <Globe size={14} className="text-accent-600" strokeWidth={1.8} />
                                    <span className="text-sm font-medium text-ink-800">{t('lang.multiLabel')}</span>
                                </div>
                                <div className="text-[11px] text-ink-500 leading-relaxed mt-1">
                                    {multiLingual
                                        ? t('lang.multiOnHint', { label: currentLangLabel })
                                        : t('lang.multiOffHint')}
                                </div>
                                {multiLingual && (
                                    <div className="text-[11px] text-amber-600 leading-relaxed mt-1">
                                        {t('lang.taigiWarn')}
                                    </div>
                                )}
                            </div>
                        </label>
                    </div>
                    )}
                    {isSurvey && (
                        <div className="border-t border-cream-200 pt-4 text-[11px] text-ink-500 leading-relaxed">
                            {t('lang.surveyInfo')}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 px-6 py-3.5 border-t border-cream-200 bg-cream-50/60">
                    <div className="text-[11px] text-ink-500">
                        {t('lang.footerNote')}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                        >
                            {t('common:actions.cancel')}
                        </button>
                        <button
                            onClick={handleApply}
                            disabled={isStreaming
                                || (language === (currentLanguage || 'zh-TW') && multiLingual === !!currentMultiLingual)}
                            className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                            <Check size={14} /> {t('lang.applyBtn')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
